import { Link, useNavigate, useParams } from 'react-router-dom'
import { ShoppingBag } from 'lucide-react'
import { MarketplaceProfile, encodePick, type BookPick } from '@adthlete/shared-frontend'
import { useAuth } from '@/hooks/useAuth'
import { useGetPublicShopQuery } from '@/redux/marketerApi'

const LOGO = 'https://adthlete.us-iad-1.linodeobjects.com/public/athios-logo-black.png'

/** Where "Book this" goes: the athlete's page in the app with the pick, through sign-in if needed */
export const bookingPath = (athleteId: string, pick: BookPick) =>
  `/athlete/${athleteId}?book=${encodeURIComponent(encodePick(pick))}`

/**
 * An athlete's Content Shop from a shared link: anyone can look, before they have an account.
 * Book this sends them to sign in (or sign up), then straight to the booking, filled in.
 */
const PublicShop = () => {
  const { id = '' } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { marketer } = useAuth()
  const { data, isLoading, isError } = useGetPublicShopQuery(id, { skip: !id })
  const signedIn = !!marketer?.id

  // The link may be their username; booking needs their id
  const athleteId = data?.athlete.id ?? id
  const book = (pick: BookPick) => {
    const to = bookingPath(athleteId, pick)
    navigate(signedIn ? to : `/signin?returnTo=${encodeURIComponent(to)}`)
  }
  const first = data?.athlete.name.split(' ')[0] ?? ''

  return (
    <div className="min-h-screen bg-[#F4F7F0]">
      <header className="sticky top-0 z-40 border-b border-[#E5E7EB] bg-[#FFFFFF]/95 backdrop-blur">
        <div className="mx-auto flex h-14 max-w-[1280px] items-center justify-between gap-3 px-4">
          <img src={LOGO} alt="Athios" className="h-7 w-auto" />
          <Link
            to={
              signedIn
                ? '/marketplace'
                : `/signin?returnTo=${encodeURIComponent(`/athlete/${athleteId}`)}`
            }
            className="rounded-xl border border-[#E5E7EB] bg-[#FFFFFF] px-3 py-1.5 text-sm font-semibold text-[#111827] hover:bg-[#F3F4F6]"
          >
            {signedIn ? 'Shop the Marketplace' : 'Sign in to book'}
          </Link>
        </div>
      </header>
      <main className="mx-auto max-w-[1280px] space-y-4 p-4 sm:p-6">
        {isLoading ? (
          <p className="text-sm text-[#6B7280]">Opening the shop…</p>
        ) : isError || !data ? (
          <div className="mx-auto max-w-md rounded-2xl border border-[#E5E7EB] bg-[#FFFFFF] p-6 text-center">
            <ShoppingBag className="mx-auto h-8 w-8 text-[#3F7A0A]" />
            <p className="mt-2 text-lg font-bold text-[#111827]">This shop isn't open</p>
            <p className="mt-1 text-sm text-[#6B7280]">
              The athlete may not be taking bookings right now. Browse other athletes' content in
              the Marketplace.
            </p>
            <Link
              to="/marketplace"
              className="mt-4 inline-flex h-10 items-center rounded-xl bg-[#C0FD72] px-4 text-sm font-semibold text-[#0F172A]"
            >
              Shop athlete content
            </Link>
          </div>
        ) : (
          <MarketplaceProfile
            data={data}
            onBook={book}
            actions={
              <>
                <button
                  type="button"
                  onClick={() => book({ kind: 'custom', themeId: null })}
                  className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-[#C0FD72] font-semibold text-[#0F172A] hover:bg-[#B2F25E]"
                >
                  <ShoppingBag className="h-4 w-4" /> Book {first}
                </button>
                {!signedIn ? (
                  <p className="text-center text-xs text-[#6B7280]">
                    Sign in or create a brand account to book.
                  </p>
                ) : null}
              </>
            }
          />
        )}
      </main>
    </div>
  )
}

export default PublicShop
